/**
 * Horizontal-scroll timeline of a participant's captures + baselines.
 *
 * Replaces the prior two-list view (separate Baselines + Sessions
 * pages) with one chronological strip per participant: each event
 * is a small icon stacked above a compact date label, clicking the
 * icon triggers the best-available download. Mixing baselines and
 * sessions in one stream is the way operators actually read the
 * data — "what did this participant do, in what order, and can I
 * pull the bytes for any of it" — so two separate lists were
 * always artificial.
 *
 * Layout:
 *   - Newest event on the left, older to the right.
 *   - `overflow-x: auto` so long histories scroll horizontally
 *     within the per-participant card; the page itself doesn't
 *     scroll sideways.
 *   - Each event renders as a vertical mini-tile: icon, then
 *     a short relative-date label (e.g. "Today 10:34", "Mon",
 *     "Jun 3", "Apr 2025"). Hover/tap shows a tooltip with the
 *     full datetime + procedure + duration.
 *
 * This file is the container: it owns the scroll box, the empty
 * state, the shared popup-menu state, and the per-event `.map`. The
 * per-tile render lives in {@link ./captureTimeline/TimelineTile},
 * the popup items in {@link ./captureTimeline/TimelineActionMenu},
 * and all the pure helpers + the data contract in
 * {@link ./captureTimeline/helpers}.
 */
import { Fragment, useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';

import type { TimelineEvent } from './captureTimeline/helpers';
import { buildMenuItems } from './captureTimeline/TimelineActionMenu';
import { TimelineTile, type MenuAnchor } from './captureTimeline/TimelineTile';
import { feedBucket, feedBucketLabel } from '../util/activityInsights';
import { useDisplayTimeZone } from '../util/displayTimeZone';

// Public API preserved for existing importers of './CaptureTimeline'
// (Dashboard, TenantActivity, TenantCognitiveSummary, activityInsights, …).
export {
  buildTimelineEvents,
  groupByDevice,
  pipelineGroups,
  withPipelines,
} from './captureTimeline/helpers';
export type { TimelineEvent, DeviceGroup } from './captureTimeline/helpers';
export { buildMenuItems } from './captureTimeline/TimelineActionMenu';

interface Props {
  events: TimelineEvent[];
  /** Id of the event whose download is currently in flight. */
  busyId?: string | null;
  /** Optional — when set, renders the per-row download icon and fires
   *  this callback when tapped. Tenant-admin drill-downs that only
   *  surface the trace card omit it; the participant Dashboard wires
   *  it for inline baseline JSON / session bytes download. */
  onDownload?: (event: TimelineEvent) => void;
  /**
   * Optional — when set, a small paperclip icon renders next to the
   * download icon for every SESSION event. Lets the caller open the
   * AttachmentsDialog without owning a separate per-row affordance.
   * Baselines don't accept attachments, so the icon is session-only.
   */
  onAttach?: (event: TimelineEvent) => void;
  /**
   * Optional — when set, an actionMenu entry lets the caller tag a
   * SESSION with its organizational context (department / location /
   * kind / external case ref). Session-only; RFC procedure-instance
   * Phase 1b.
   */
  onTag?: (event: TimelineEvent) => void;
  /**
   * Optional — when set, the whole tile (outside the inline action
   * icons) becomes clickable and fires this callback. The parent uses
   * it to surface a per-event detail card (e.g. the participant
   * Dashboard's "cognitive trace" view for a selected session).
   * Inline download / attach icons stop propagation so a single click
   * never both selects AND downloads.
   */
  onSelect?: (event: TimelineEvent) => void;
  /** Id of the currently-selected event — gets a primary-color
   *  highlight border. Only meaningful when `onSelect` is set. */
  selectedId?: string | null;
  /**
   * Optional — when set, a per-row Disable (live rows) / Restore
   * (soft-deleted rows) icon renders. The caller confirms + calls the
   * disable/restore API then refetches. Tenant-admin management surface
   * only; read-only views omit it.
   */
  onDisable?: (event: TimelineEvent) => void;
  onRestore?: (event: TimelineEvent) => void;
  /**
   * Optional — when set, a Replay (play) icon renders on every SESSION
   * event. Fires this callback so the caller can open the in-browser
   * replay. Baselines aren't replayable, so the icon is session-only.
   */
  onReplay?: (event: TimelineEvent) => void;
  /**
   * Optional — adds a "Split into sessions" item for SESSION events that
   * have a recording and aren't disabled (SESSION-SPLIT). Fires so the
   * caller can open the guided split dialog.
   */
  onSplit?: (event: TimelineEvent) => void;
  /**
   * Optional — in `actionMenu` mode, adds a "View performance trace"
   * item for SESSION events. The participant popup uses it to open the
   * inline cognitive-trace card; a plain `onSelect` (tile-click select)
   * can't coexist with the action menu, so the select moves in here.
   */
  onViewTrace?: (event: TimelineEvent) => void;
  /**
   * When true, the session Download item is omitted from the action
   * menu (baseline download still shows). Participants can replay +
   * attach but not download a session recording.
   */
  hideSessionDownload?: boolean;
  /**
   * When true, the per-event actions are NOT rendered inline; instead
   * each tile becomes clickable and opens a popup menu with the
   * available actions. Keeps a long Activity stream from repeating the
   * same icon column on every tile. Ignored when `onSelect` is set
   * (that surface uses click-to-select, not an action menu).
   */
  actionMenu?: boolean;
  /**
   * When true, a RIGHT-CLICK on a tile opens the same action popup — while a
   * normal left-click still fires `onSelect`. Lets a surface offer
   * click-to-select (e.g. open the metric scorecard) AND the full action menu
   * without the two fighting over the left click. Coexists with `onSelect`;
   * independent of `actionMenu` (the left-click popup). `onViewTrace` is
   * unnecessary here — the left-click select already opens the trace.
   */
  contextMenu?: boolean;
  /** Empty-state copy when this participant has no captures. */
  emptyText?: string;
  /**
   * Layout. 'horizontal' (default) is the scrolling strip; 'vertical' stacks
   * the tiles into a scrollable column — used as a session-picker rail beside
   * the scorecard (master-detail).
   */
  orientation?: 'horizontal' | 'vertical';
  /** Max height (px) of the scroll area in vertical mode. */
  maxHeight?: number;
  /**
   * Vertical mode only: stretch the scroll column to 100% of its parent
   * (md+) instead of the fixed `maxHeight`, so a picker rail can match the
   * height of the metrics card beside it and scroll internally. Falls back
   * to `maxHeight` on the stacked (xs) layout.
   */
  fillHeight?: boolean;
  /**
   * Vertical mode only: interleave date-bucket subheaders (Today / Yesterday
   * / Earlier this week / …) between tiles, so a long history reads in
   * sections instead of one undifferentiated column. Events must already be
   * newest-first (buildTimelineEvents guarantees this).
   */
  groupByDate?: boolean;
  /**
   * When set, each tile shows a small cross-dimension line naming the OTHER
   * axis than the one the caller grouped on — so the context isn't lost once
   * events are bucketed. `'device'` renders the capture device (used in the
   * by-participant view), `'participant'` renders who recorded it (used in
   * the by-device view). Rows with a null value for the chosen axis render
   * nothing. Omit it (the default) to keep tiles bare.
   */
  secondaryLabel?: 'device' | 'participant';
}

export function CaptureTimeline({
  events,
  busyId,
  onDownload,
  onAttach,
  onTag,
  onSelect,
  selectedId,
  onDisable,
  onRestore,
  onReplay,
  onSplit,
  onViewTrace,
  hideSessionDownload = false,
  actionMenu = false,
  contextMenu = false,
  emptyText = 'No captures or baselines yet.',
  orientation = 'horizontal',
  maxHeight = 460,
  fillHeight = false,
  groupByDate = false,
  secondaryLabel,
}: Props) {
  const vertical = orientation === 'vertical';
  const displayTimeZone = useDisplayTimeZone();
  // Shared popup-menu state — which tile's menu is open, anchored either to
  // the tile (left-click actionMenu) or the cursor (right-click contextMenu).
  const [menuState, setMenuState] = useState<
    (MenuAnchor & { event: TimelineEvent }) | null
  >(null);
  // onSelect (click-to-select) takes precedence over the action menu;
  // the menu mode is only meaningful on non-selectable surfaces.
  const useActionMenu = actionMenu && onSelect === undefined;

  if (events.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box
      sx={
        vertical
          ? {
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              py: 1,
              gap: 1,
              // fillHeight: stretch to the parent's height on the row
              // layout (md+) so the rail matches the metrics card, but
              // keep the fixed cap on the stacked (xs) layout.
              ...(fillHeight
                ? {
                    height: { xs: 'auto', md: '100%' },
                    maxHeight: { xs: maxHeight, md: '100%' },
                  }
                : { maxHeight }),
            }
          : {
              display: 'flex',
              overflowX: 'auto',
              // Hide the horizontal scrollbar visual on hover-only inputs
              // but keep it for touch — operators on a kiosk see a finger;
              // operators on the portal see a trackpad.
              py: 1,
              gap: 1.5,
            }
      }
    >
      {events.map((event, idx) => {
        // Date-bucket subheader (vertical + groupByDate) — shown when this
        // event opens a new bucket. Events are newest-first, so buckets are
        // contiguous.
        const bucketHeader =
          vertical && groupByDate
            ? (() => {
                const bucket = feedBucket(event.capturedAt);
                const prevBucket =
                  idx > 0 ? feedBucket(events[idx - 1].capturedAt) : null;
                return bucket !== prevBucket ? feedBucketLabel(bucket) : null;
              })()
            : null;
        return (
          <Fragment key={event.id}>
            {bucketHeader && (
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  px: 1,
                  pt: idx === 0 ? 0 : 1.5,
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              >
                {bucketHeader}
              </Typography>
            )}
            <TimelineTile
              event={event}
              displayTimeZone={displayTimeZone}
              busyId={busyId}
              selectedId={selectedId}
              vertical={vertical}
              hideSessionDownload={hideSessionDownload}
              useActionMenu={useActionMenu}
              contextMenu={contextMenu}
              secondaryLabel={secondaryLabel}
              onDownload={onDownload}
              onAttach={onAttach}
              onTag={onTag}
              onSelect={onSelect}
              onDisable={onDisable}
              onRestore={onRestore}
              onReplay={onReplay}
              onSplit={onSplit}
              onViewTrace={onViewTrace}
              onOpenMenu={(evt, where) =>
                setMenuState({ event: evt, ...where })
              }
            />
          </Fragment>
        );
      })}
      {/* Shared popup menu for actionMenu mode — anchored to whichever
          tile was clicked. Items reflect that event's available
          actions. */}
      <Menu
        anchorReference={menuState?.position ? 'anchorPosition' : 'anchorEl'}
        anchorEl={menuState?.anchor ?? null}
        anchorPosition={menuState?.position}
        open={Boolean(menuState)}
        onClose={() => setMenuState(null)}
      >
        {menuState
          ? buildMenuItems(menuState.event, {
              onDownload,
              onAttach,
              onTag,
              onDisable,
              onRestore,
              onReplay,
              onSplit,
              onViewTrace,
              hideSessionDownload,
              close: () => setMenuState(null),
            })
          : null}
      </Menu>
    </Box>
  );
}
